import { holidayName } from '@till-erp/platform/workdays';

/**
 * Reine Hilfen für das Datumsfeld mit Kalender (DESIGN U23, Ticket 1.215): Wert ist ein ISO-Kalendertag `JJJJ-MM-TT`
 * (Europe/Berlin), Anzeige `TT.MM.JJJJ`. Gerechnet wird nur in ganzen UTC-Tagen (`Date.UTC`), nie mit der lokalen
 * Zeitzone; keine Uhr – „heute“ kommt immer von außen. Feiertage Bayern nur über `holidayName` aus `platform`.
 */

const DAY_MS = 86_400_000;
/** Ältestes Jahr, das der Feiertagskalender kennt (gregorianisch); jüngstes vierstelliges Jahr. */
const FIRST_YEAR = 1583;
const LAST_YEAR = 9999;
/**
 * Rein technischer Bereich, den das Feld annimmt und in den der Kalender blättert (keine fachliche Grenze – die kommt
 * als `min`/`max` von der Seite): `FIRST_YEAR`/`LAST_YEAR` mit je einem Jahr Puffer, damit die aufgefüllten Tage des
 * Vor- und Folgemonats im Raster immer rechenbar bleiben (kein Jahr 1582 oder 10000).
 */
export const EARLIEST_DATE = `${FIRST_YEAR + 1}-01-01`;
export const LATEST_DATE = `${LAST_YEAR - 1}-12-31`;

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as const;
/** Wochentage ab Montag (Kalender Mo–So). */
export const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;
const WEEKDAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'] as const;

export type ParsedDate = { iso: string } | { error: 'format' } | { empty: true };

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/** Gültiger Kalendertag? Liefert das ISO-Datum oder `null` (z. B. 30.02., 32.01., 29.02. außerhalb Schaltjahr). */
function isoOf(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || year < FIRST_YEAR || year > LAST_YEAR) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/** Teile eines ISO-Datums; `null`, wenn es kein gültiger Tag ist. */
export function partsOf(iso: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  return isoOf(year, month, day) ? { year, month, day } : null;
}

function toDay(iso: string): number {
  const p = partsOf(iso);
  if (!p) throw new RangeError(`Kein gültiges Datum (erwartet JJJJ-MM-TT): ${iso}`);
  return Date.UTC(p.year, p.month - 1, p.day) / DAY_MS;
}

function fromDay(day: number): string {
  const d = new Date(day * DAY_MS);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function accepted(iso: string | null): ParsedDate {
  return iso && iso >= EARLIEST_DATE && iso <= LATEST_DATE ? { iso } : { error: 'format' };
}

/**
 * Getippten Text lesen: „26.02.2027“, „26.2.27“ (zweistelliges Jahr → 20JJ), „1.3.2027“ oder ISO „2027-02-26“.
 * Leer → `{ empty: true }`; sonst ungültig → `{ error: 'format' }`.
 */
export function parseDateInput(text: string): ParsedDate {
  const t = text.trim();
  if (t === '') return { empty: true };
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (iso) {
    return accepted(isoOf(Number(iso[1]), Number(iso[2]), Number(iso[3])));
  }
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(t);
  if (de) {
    const yearText = de[3]!;
    const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);
    return accepted(isoOf(year, Number(de[2]), Number(de[1])));
  }
  return { error: 'format' };
}

/** ISO → „TT.MM.JJJJ“; kein gültiges Datum → leerer Text. */
export function formatDate(iso: string): string {
  const p = partsOf(iso);
  return p ? `${pad(p.day)}.${pad(p.month)}.${pad(p.year, 4)}` : '';
}

/** Tage addieren (negativ = zurück). */
export function addDays(iso: string, days: number): string {
  return fromDay(toDay(iso) + days);
}

/** Monate addieren; der Tag wird auf das Monatsende begrenzt (31.01. + 1 Monat = 28.02. bzw. 29.02.). */
export function addMonths(iso: string, months: number): string {
  const p = partsOf(iso);
  if (!p) throw new RangeError(`Kein gültiges Datum (erwartet JJJJ-MM-TT): ${iso}`);
  const index = p.year * 12 + (p.month - 1) + months;
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${pad(year, 4)}-${pad(month)}-${pad(Math.min(p.day, last))}`;
}

/** Wochentag 0 = Montag … 6 = Sonntag. */
export function weekdayIndex(iso: string): number {
  return (new Date(toDay(iso) * DAY_MS).getUTCDay() + 6) % 7;
}

export interface GridDay {
  iso: string;
  /** Gehört der Tag zum gezeigten Monat (sonst Vor-/Folgemonat zum Auffüllen der Woche)? */
  inMonth: boolean;
}

/** Monatsansicht: Wochen von Montag bis Sonntag, aufgefüllt mit Tagen des Vor- und Folgemonats. `month` 1–12. */
export function monthGrid(year: number, month: number): GridDay[][] {
  const first = `${pad(year, 4)}-${pad(month)}-01`;
  const start = toDay(first) - weekdayIndex(first);
  const lastDay = toDay(addDays(addMonths(first, 1), -1));
  const weeks: GridDay[][] = [];
  for (let day = start; day <= lastDay; day += 7) {
    weeks.push(
      Array.from({ length: 7 }, (_, i) => {
        const iso = fromDay(day + i);
        return { iso, inMonth: Number(iso.slice(5, 7)) === month };
      }),
    );
  }
  return weeks;
}

/** Kopf des Kalenders: „Februar 2027“. */
export function monthTitle(year: number, month: number): string {
  return `${MONTHS[month - 1] ?? ''} ${year}`;
}

/** Name eines Tages für Hilfsmittel: „Freitag, 26. Februar 2027“. */
export function dayLabel(iso: string): string {
  const p = partsOf(iso);
  if (!p) return '';
  return `${WEEKDAYS_LONG[weekdayIndex(iso)]}, ${p.day}. ${MONTHS[p.month - 1]} ${p.year}`;
}

/** Wochenende oder Feiertag in Bayern (Kalender `platform`, keine zweite Regel)? */
export function isOffDay(iso: string): boolean {
  if (weekdayIndex(iso) >= 5) return true;
  return holidayName(iso) !== null;
}

/** Liegt `iso` außerhalb von `min`/`max` (beide ISO, optional)? */
export function outOfRange(iso: string, min?: string, max?: string): 'min' | 'max' | null {
  if (min && iso < min) return 'min';
  if (max && iso > max) return 'max';
  return null;
}

/** Engere von zwei Grenzen: späteste Untergrenze bzw. früheste Obergrenze (fehlend = keine). */
export function laterOf(a: string | undefined, b: string): string {
  return a && a > b ? a : b;
}
export function earlierOf(a: string | undefined, b: string): string {
  return a && a < b ? a : b;
}

/** Tag auf `min`/`max` begrenzen. */
export function clampDate(iso: string, min?: string, max?: string): string {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}
